import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { AuthKitProvider } from '@workos-inc/authkit-react'
import { ThemeProvider } from 'next-themes'
import * as Sentry from '@sentry/react'
import type { ErrorEvent } from '@sentry/react'

// Import the generated route tree
import { routeTree } from './routeTree.gen'

import './index.css'

// Initialize Sentry (disabled in development mode)
if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.MODE !== 'development') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // Capture 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors
    // PII scrubbing
    beforeSend(event: ErrorEvent) {
      // Remove query string parameters that might contain tokens
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url)
          url.search = '' // Remove all query parameters
          event.request.url = url.toString()
        } catch {
          // Invalid URL, leave as is
        }
      }
      return event
    },
  })
}

// Create a query client
// Auth errors are handled in useAuthenticatedClient() which calls signIn() on failure
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      // Retry up to 3 times for network/timeout errors
      // This helps ride out a slow or restarting backend
      retry: 3,
      // Exponential backoff: 1s, 2s, 4s between retries
      // Gives the server time to fully wake up
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
    },
    mutations: {
      // Mutations should also retry for network errors
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
    },
  },
})

// Create the router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// WorkOS AuthKit configuration
const WORKOS_CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID
const WORKOS_REDIRECT_URI = import.meta.env.VITE_WORKOS_REDIRECT_URI || undefined
const WORKOS_API_HOSTNAME = import.meta.env.VITE_WORKOS_API_HOSTNAME || undefined

// Determine if we should use devMode
// devMode stores refresh token in localStorage instead of HttpOnly cookie
// Required when NOT using a custom authentication domain (apiHostname)
const useDevMode = !WORKOS_API_HOSTNAME

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<div>An error has occurred</div>}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <AuthKitProvider
          clientId={WORKOS_CLIENT_ID}
          redirectUri={WORKOS_REDIRECT_URI}
          apiHostname={WORKOS_API_HOSTNAME}
          devMode={useDevMode}
        >
          <QueryClientProvider client={queryClient}>
            <NuqsAdapter>
              <RouterProvider router={router} />
            </NuqsAdapter>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </AuthKitProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
